import { useState, useEffect } from 'react';
import { Box, Typography, useTheme, keyframes } from '@mui/material';
import Icon from '../shared/Icon.jsx';

const pulse = keyframes`
  0% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  50% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
`;

const shimmer = keyframes`
  0% {
    background-position: 200% center;
  }
  100% {
    background-position: -200% center;
  }
`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-5px);
  }
  100% {
    opacity: 0.2;
    transform: translateY(0);
  }
`;

const loadingMessages = [
  "Crunching numbers...",
  "Thinking hard…",
  "Arranging bits…",
  "Sorting insights…",
  "Thinking deeply…",
  "Connecting the dots…",
  "Brewing insights…",
];

const dadJokes = [
  "Why did the spreadsheet go to therapy? Too many issues to cell…",
  "What's a franchisee's favorite exercise? Running multiple locations…",
  "Why don't accountants read novels? The only numbers in them are page numbers…",
  "Why did the employee get fired from the calendar factory? He took a day off…",
  "What do you call a bear with no data? A bear-ly informed decision maker…",
  "Why did the pie chart go to the doctor? It needed more segments…",
  "Why did the spreadsheet break up with the calculator? There were too many issues to count…",
  "What do you call a fake noodle? An impasta…",
  "Why don't scientists trust atoms? They make up everything…",
  "Why did the scarecrow win an award? He was outstanding in his field…",
  "What do you call a belt made of watches? A waist of time…",
  "Why did the coffee file a police report? It got mugged…",
  "Why did the tomato turn red? It saw the salad dressing…",
  "What do you call cheese that isn't yours? Nacho cheese…",
  "Why did the cookie go to the hospital? It felt crumbly…",
  "What did the sandwich say to the doorman? Lettuce in…",
  "Why don't eggs tell jokes? They'd crack each other up…",
  "What did the profit say to the loss? Stop being so negative…",
  "Why don't donuts ever start businesses? They're afraid of the hole investment…",
  "I only know 25 letters of the alphabet. I don't know y…",
  "Why do chicken coops only have two doors? Because if they had four, they'd be chicken sedans…",
  "What did the ocean say to the beach? Nothing, it just waved…",
  "Did you hear about the restaurant on the moon? Great food, no atmosphere…",
];

const TypingIndicator = ({ isProcessing }) => {
  const theme = useTheme();
  const [loadingText, setLoadingText] = useState(loadingMessages[0]);
  const [displayedText, setDisplayedText] = useState('');
  const [dadJoke, setDadJoke] = useState(dadJokes[0]);
  const [displayedJoke, setDisplayedJoke] = useState('');
  const [showIcon, setShowIcon] = useState(false);

  // Pick a random loading message and dad joke when processing starts
  useEffect(() => {
    if (isProcessing) {
      const randomMessage = loadingMessages[Math.floor(Math.random() * loadingMessages.length)];
      setLoadingText(randomMessage);
      setDisplayedText(''); // Reset displayed text

      const randomJoke = dadJokes[Math.floor(Math.random() * dadJokes.length)];
      setDadJoke(randomJoke);
      setDisplayedJoke(''); // Reset displayed joke
      setShowIcon(false); // Reset icon visibility
    }
  }, [isProcessing]);

  // Typing effect animation for loading text
  useEffect(() => {
    if (!isProcessing || !loadingText) return;

    let currentIndex = 0;
    setDisplayedText('');

    const typingInterval = setInterval(() => {
      if (currentIndex < loadingText.length) {
        setDisplayedText(loadingText.slice(0, currentIndex + 1));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
      }
    }, 25); // 25ms per character

    return () => clearInterval(typingInterval);
  }, [loadingText, isProcessing]);

  // Typing effect animation for dad joke (delayed)
  useEffect(() => {
    if (!isProcessing || !dadJoke) return;

    let currentIndex = 0;
    let typingInterval;
    let iconTimeout;
    setDisplayedJoke('');

    // Calculate delay: wait for loading text to finish + small pause
    const loadingTextDuration = loadingText.length * 25;
    const pauseBeforeIcon = 200; // Pause before showing icon
    const iconFadeInDuration = 300; // Time for icon to fade in
    const iconDelay = loadingTextDuration + pauseBeforeIcon;
    const jokeDelay = iconDelay + iconFadeInDuration; // Show joke after icon fades in

    // Show icon first
    iconTimeout = setTimeout(() => {
      setShowIcon(true);
    }, iconDelay);

    // Then start typing the joke
    const delayTimeout = setTimeout(() => {
      typingInterval = setInterval(() => {
        if (currentIndex < dadJoke.length) {
          setDisplayedJoke(dadJoke.slice(0, currentIndex + 1));
          currentIndex++;
        } else {
          clearInterval(typingInterval);
        }
      }, 20); // 20ms per character - slightly faster than loading text
    }, jokeDelay);

    return () => {
      clearTimeout(iconTimeout);
      clearTimeout(delayTimeout);
      if (typingInterval) {
        clearInterval(typingInterval);
      }
    };
  }, [dadJoke, loadingText, isProcessing]);

  if (!isProcessing) return null;

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
    }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}>
        <Box
          sx={{
            width: '1.125rem',
            height: '1.125rem',
            borderRadius: '50%',
            backgroundColor: theme.palette.primary.main,
            animation: `${pulse} 1.5s ease-in-out infinite`,
          }}
        />
        <Typography
          variant="body2"
          sx={{
            background: `linear-gradient(90deg,
              ${theme.palette.text.secondary} 0%,
              ${theme.palette.primary.main} 50%,
              ${theme.palette.text.secondary} 100%)`,
            backgroundSize: '200% auto',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            animation: `${shimmer} 2s linear infinite`,
            fontWeight: 500,
          }}
        >
          {displayedText}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.65 }}>
        <Icon
          name="corner-arrow"
          size={20}
          sx={{
            marginLeft: '0.25rem',
            marginBottom: '0.75rem',
            opacity: showIcon ? 0.2 : 0,
            color: 'text.secondary',
            animation: showIcon ? `${fadeIn} 0.3s ease-out forwards` : 'none',
          }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            flex: '1',
            fontStyle: 'italic',
            fontSize: '0.875rem',
            lineHeight: '1.2',
            opacity: 0.75,
            alignSelf: 'center',
          }}
        >
          {displayedJoke}
        </Typography>
      </Box>
    </Box>
  );
};

export default TypingIndicator;
